import React from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import Stack from "@mui/material/Stack"
import { styled, Theme } from "@mui/material/styles"
import PAGES from "constants/pages"
import { AppTheme } from "types/general.types"
import { PremiumSlide } from "types/premium.types"
import { updateShowPremiumOnboarding } from "appRedux/slices/premiumSlice"
import Logo from "components/atoms/_icons/Logo"
import CancelIcon from "components/atoms/_icons/CancelIcon"
import PremiumLogo from "components/atoms/_icons/PremiumLogo"
import CTA from "components/atoms/CTA"
import LazyImage from "components/atoms/LazyImage"

type OnboardingSlideProps = {
  data: PremiumSlide;
  onClose: () => void;
}

export default function PremiumOnboardingSlide(props: OnboardingSlideProps) {
  const { data, onClose } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  function handlePurchaseSubscriptionButtonClicked() {
    navigate(PAGES.premiumSubscription.path)
    dispatch(updateShowPremiumOnboarding(false))
  }

  return (
    <Container mode={data.theme}>
      <BackgroundVectors>
        <TopVectors mode={data.theme}/>
        <BottomVectors mode={data.theme}/>
      </BackgroundVectors>
      <Wrapper>
        <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
          {data.key !== "main" && <Logo/>}
          <CloseIconWrapper mode={data.theme} onClick={onClose}>
            <CancelIcon/>
          </CloseIconWrapper>
        </Stack>
        <ContentContainer mode={data.theme}>
          <Stack alignItems="center" justifyContent="center" flexGrow={1}>
            {data.imageName ? <LazyImage
              src={`/static/images/premium/${data.imageName}.png`}
              alt={data.imageName}
              style={{ objectFit: "contain", height: "auto" }}
            /> : <PremiumLogo/>}
            <Text mode={data.theme}>
              <p className="title">{t(`premium.slides.${data.key.toLowerCase()}.title`)}</p>
              {data.key !== "main" && <p className="description">{t(`premium.slides.${data.key.toLowerCase()}.description`)}</p>}
            </Text>
          </Stack>
          <CTA
            variant="contained"
            onClick={handlePurchaseSubscriptionButtonClicked}
          >
            {t("premium.purchaseSubscription")}
          </CTA>
        </ContentContainer>
      </Wrapper>
    </Container>
  )
}

type ContainerProps = {
  theme?: Theme;
  mode: AppTheme;
}

const Container = styled(Stack)(({ theme, mode }: ContainerProps) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: " 100vh",
  backgroundColor: mode === "light" ? theme?.palette.primary["1"] : theme?.palette.primary["8"],
  padding: "16px 16px 24px",
  overflow: "hidden",

  "& svg#logo": {
    width: "40px !important",
    height: "40px !important",

    "& path": {
      fill: mode === "light" ? theme?.palette.primary["6"] : theme?.palette.gray["1"]
    }
  },

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const BackgroundVectors = styled("div")(() => ({
  position: "absolute",
  top: 0,
  right: 0,
  width: "100%",
  height: "100%"
}))

const TopVectors = styled("div")(({ theme, mode }: ContainerProps) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  right: 0,

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    right: 0,
    backgroundImage: mode === "light" ? "url('/static/images/abstract-vectors/wave-vector-light.svg')" : "url('/static/images/abstract-vectors/wave-vector-dark.svg')",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    backgroundPosition: "top right",
    transform: "rotate(0deg)",
    zIndex: 1
  }
}))

const BottomVectors = styled("div")(({ theme, mode }: ContainerProps) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  bottom: 0,
  right: 0,

  "&::before": {
    content: "''",
    width: "100%",
    height: "100%",
    position: "absolute",
    bottom: 0,
    right: 0,
    backgroundImage: mode === "light" ? "url('/static/images/abstract-vectors/wave-vector-light.svg')" : "url('/static/images/abstract-vectors/wave-vector-dark.svg')",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    backgroundPosition: "top left",
    transform: "rotate(180deg)",
    zIndex: 1
  }
}))

const Wrapper = styled(Stack)(() => ({
  height: "100%",
  zIndex: 10
}))

const CloseIconWrapper = styled(Stack)(({ theme, mode }: ContainerProps) => ({
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  backgroundColor: mode === "light" ? theme?.palette.primary["4"] : theme?.palette.primary["7"],
  marginRight: "auto",

  "& svg path": {
    fill: "#FFFFFF !important"
  }
}))

const ContentContainer = styled(Stack)(({ theme, mode }: ContainerProps) => ({
  width: "100%",
  height: "100%",
  justifyContent: "center",
  alignItems: "center",
  flexGrow: 1,
  direction: "ltr",

  "& img": {
    maxWidth: "calc(var(--max-container-width) * 0.72)",

    "@media (max-height: 740px)": {
      maxWidth: "calc(var(--max-container-width) * 0.5)"
    }
  },

  "& button": {
    maxWidth: "calc(var(--max-container-width) * 0.64)",
    margin: "auto 55px 54px",
    backgroundColor: mode === "light" ? theme?.palette.primary["6"] : theme?.palette.primary["5"]
  }
}))

const Text = styled(Stack)(({ theme, mode }: ContainerProps) => ({
  justifyContent: "center",
  alignItems: "center",
  color: mode === "light" ? theme?.palette.primary["7"] : "#FFFFFF",
  textAlign: "center",

  "& .title": {
    fontSize: "1.8rem",
    fontWeight: 600,
    marginTop: "32px",
    marginBottom: "16px",

    "@media (max-height: 568px)": {
      fontSize: "1.6rem"
    }
  },

  "& .description": {
    fontSize: "1.3rem",
    fontWeight: 400,
    padding: "0 3.8rem",

    "@media (max-height: 568px)": {
      padding: "0 3rem",
      fontSize: "1.1rem"
    }
  }
}))
